import { useCallback } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useRagnar } from '@mentimeter/ragnar-react';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { useUniqueSelector } from '../../../shared/hooks/useUniqueSelector';
import { TitleSection } from '../../../shared/components/title-section/TitleSection';
import { getData } from './utils/getData';
import { LegacyWordCloudVisualization } from './LegacyWordCloudVisualization';
import { WordCloudVisualization } from './WordCloudVisualization';

// copy pasted for now
const WORD_CLOUD_TEXTS = [
  { text: 'creative', count: 8 },
  { text: 'bold', count: 3 },
  { text: 'fast', count: 8 },
  { text: 'Inspiration', count: 1 },
  { text: 'transpiration', count: 2 },
  { text: 'leader', count: 5 },
  { text: 'focus', count: 4 },
  { text: 'leadership', count: 2 },
  { text: 'quick', count: 3 },
  { text: 'inspirational', count: 2 },
  { text: 'inspire ', count: 3 },
  { text: 'top', count: 2 },
  { text: 'quality', count: 5 },
  { text: 'sharing', count: 2 },
  { text: 'leading', count: 2 },
  { text: 'integration', count: 2 },
  { text: 'innovative', count: 1 },
  { text: 'performance', count: 1 },
  { text: 'innovation', count: 1 },
  { text: 'transformers', count: 1 },
  { text: 'change', count: 1 },
];

const NUMBER_OF_ANSWERS = 7;
const PREVIEW_ANSWERS = WORD_CLOUD_TEXTS.slice(0, NUMBER_OF_ANSWERS);
const PREVIEW_DATA = getData(PREVIEW_ANSWERS);

interface EditorEmptyStateProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  isNewLookFlagEnabled: boolean;
}

export const EditorEmptyState = ({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  CanvasVisualizationArea,
  isNewLookFlagEnabled,
}: EditorEmptyStateProps) => {
  const selector = useUniqueSelector();

  const {
    theme: {
      fonts: { body: font },
      visualizationColors: { fillColors },
    },
  } = useRagnar();

  const ConditionalWrapper = CanvasVisualizationArea ?? Box;

  const handleRemoveWord = useCallback(() => {}, []);

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="presentation-container"
      justifyContent="center"
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />

      <ConditionalWrapper
        flex="1"
        width="100%"
        height="75%"
        extend={() => ({
          '& > svg': {
            opacity: 0.3,
          },
        })}
      >
        {isNewLookFlagEnabled ? (
          <WordCloudVisualization
            shouldAnimate={false}
            shouldHideResults={false}
            results={PREVIEW_DATA.map((item) => ({
              text: item.text,
              value: item.value ?? 0,
            }))}
            onRemoveWord={undefined}
          />
        ) : (
          <LegacyWordCloudVisualization
            selector={`empty-word-cloud-${selector}`}
            data={PREVIEW_DATA}
            emojiCloud={false}
            fillColors={fillColors}
            shouldAnimate={false}
            font={font}
            shouldShowResults={true}
            isModerationEnabled={false}
            onRemoveWord={handleRemoveWord}
          />
        )}
      </ConditionalWrapper>
    </Box>
  );
};
